import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';

import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Exams from './pages/Exams';
import ExamDetails from './pages/ExamDetails';
import ExamInfo from './pages/ExamInfo';
import TestCreate from './pages/TestCreate';
import Test from './pages/Test';
import NTATest from './pages/NTATest';
import TestResults from './pages/TestResults';
import Mistakes from './pages/Mistakes';
import Bookmarks from './pages/Bookmarks';
import Analytics from './pages/AdvancedAnalytics';
import Friends from './pages/Friends';
// Study Rooms removed - using Challenge a Friend feature instead
import PYQBrowser from './pages/PYQBrowser';
import Profile from './pages/Profile';
import PracticeNew from './pages/PracticeNew';
import Subscription from './pages/Subscription';
import Challenges from './pages/Challenges';
import ChallengeLobby from './pages/ChallengeLobby';
import ChallengeTest from './pages/ChallengeTest';
import PracticeWithFriend from './pages/PracticeWithFriend';
import Admin from './pages/Admin';
import QuestionManager from './pages/QuestionManager';
import EditorPanel from './pages/EditorPanel';
import AuthCallback from './pages/AuthCallback';
import LoadingSpinner from './components/LoadingSpinner';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading..." fullScreen={true} />;
  }

  return isAuthenticated ? children : <Navigate to="/auth?mode=login" />;
};

// Admin-only route protection (strict - only admin role)
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading..." fullScreen={true} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth?mode=login" />;
  }

  // Only admin role allowed
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Content Editor route protection (content_editor or admin role)
const EditorRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading..." fullScreen={true} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth?mode=login" />;
  }

  // Only content_editor or admin role allowed
  if (user?.role !== 'content_editor' && user?.role !== 'admin') {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Guest can take test but needs login for results
const GuestTestRoute = ({ children }) => {
  return children;
};

// Results require authentication
const ResultsRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner size="lg" text="Loading..." fullScreen={true} />;
  }
  
  // If not authenticated, redirect to auth with return URL
  if (!isAuthenticated) {
    return <Navigate to="/auth?mode=login&returnTo=results" />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/exams" element={<Exams />} />
            <Route path="/exam/:examId" element={<ExamDetails />} />
            <Route path="/exam-info/:examId" element={<ExamInfo />} />
            <Route path="/test/create" element={<TestCreate />} />
            <Route path="/practice" element={<PracticeNew />} />
            <Route path="/practice/:examId" element={<PracticeNew />} />
            
            {/* Guest can take test - NTA Style for full tests */}
            <Route path="/test/:testId" element={<GuestTestRoute><NTATest /></GuestTestRoute>} />
            <Route path="/test/:testId/classic" element={<GuestTestRoute><Test /></GuestTestRoute>} />
            
            {/* Results require login */}
            <Route path="/test/:testId/results" element={<ResultsRoute><TestResults /></ResultsRoute>} />
            
            <Route path="/pyq-browser" element={<PYQBrowser />} />
            <Route path="/pyqs" element={<PYQBrowser />} />
            <Route path="/subscription" element={<Subscription />} />
            
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mistakes"
              element={
                <ProtectedRoute>
                  <Mistakes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookmarks"
              element={
                <ProtectedRoute>
                  <Bookmarks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <Analytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/friends"
              element={
                <ProtectedRoute>
                  <Friends />
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenges"
              element={
                <ProtectedRoute>
                  <Challenges />
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenge/:code"
              element={
                <ProtectedRoute>
                  <ChallengeLobby />
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenge/:code/test"
              element={
                <ProtectedRoute>
                  <ChallengeTest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/practice-friend"
              element={
                <ProtectedRoute>
                  <PracticeWithFriend />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/questions"
              element={
                <AdminRoute>
                  <QuestionManager />
                </AdminRoute>
              }
            />
            <Route
              path="/editor"
              element={
                <EditorRoute>
                  <EditorPanel />
                </EditorRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
